import { defaultTheme, RADIUS_MD, RADIUS_SM } from './buildAntdTheme';
import { hireportDarkColors, hireportDarkSurfaces, mixHex } from './colors';

import type { HirePortTheme } from './buildAntdTheme';

export const packs = [
  'recruitee',
  'jobylon',
  'smartrecruiters',
  'hibob',
] as const;

export type PackName = (typeof packs)[number];

const packNameSet = new Set<string>(packs);

/**
 * Reads ATS pack from `/embed/{pack}/…`. Returns undefined for non-embed paths,
 * `/embed/activate`, or unknown pack segments.
 */
export function getEmbedPackFromPathname(
  pathname: string,
): PackName | undefined {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'embed') return undefined;
  const candidate = segments[1];
  if (!candidate || !packNameSet.has(candidate)) return undefined;
  return candidate as PackName;
}

const RECRUITEE_PRIMARY = '#5A1CB7';
const RECRUITEE_DARK_BG = '#2d2e30';
const RECRUITEE_DARK_TEXT = '#c9cbcf';
const RECRUITEE_DARK_PRIMARY = '#af80f4';
const RECRUITEE_DARK_PRIMARY_HOVER = '#ba92f6';
const RECRUITEE_DARK_PRIMARY_ACTIVE = '#8e6ac5';
const JOBYLON_PRIMARY = '#0068FF';
const JOBYLON_SECONDARY = '#045CDB';
const SMARTRECRUITERS_PRIMARY = '#238740';
const SMARTRECRUITERS_SECONDARY = '#085723';
/**
 * HiBob Robin system colors (Foundations/Colors).
 * https://robin.hibob.com/?path=/docs/foundations-colors--docs
 */
const HIBOB_PRIMARY = '#D01D73'; // primary-600 — actions / navigation
const HIBOB_PRIMARY_HOVER = '#C20D64'; // primary-700
const HIBOB_PRIMARY_DARK = '#83143D'; // illustrations-600 — dark enough for white text
const HIBOB_CAPPUCCINO = '#FAF7F2';
const HIBOB_GREY_600 = '#6C6C6C';
const PARTNER_DISABLED_SWITCH = '#64748B';

/** Opaque 10% blends on #FFFFFF — legacy rgba tints must be solid for popover surfaces. */
const RECRUITEE_PRIMARY_BG = '#EFE8F8'; // #5A1CB7 @ 10%
const JOBYLON_LOZENGE = '#E6F0FF'; // #0068FF @ 10% (legacy lozengeColor)
const JOBYLON_GHOST_BG = '#E8F2F8'; // #147AB6 @ 10% (legacy ghostHoverColor)
const SMARTRECRUITERS_PRIMARY_BG = '#E6EEE9'; // #085723 @ 10%
const HIBOB_PRIMARY_BG = '#FFE3E9'; // illustrations-200

/**
 * ATS partner shell palettes (Layer 2 tenant tokens).
 * Source of truth: docs/design-tokens.md — Tenant shell overrides.
 *
 * Legacy → v2: primaryColor→colorPrimary, hoverColor→colorSecondary,
 * activeColor→colorThird, lozengeColor→colorBrightness, ghostHoverColor→colorPrimaryBg.
 *
 * colorThird drives Tooltip/Popover spotlight (white text) — must be a dark brand hue,
 * not a light lozenge tint. Light legacy activeColor tints belong on colorBrightness only.
 */
export const partnerThemes: Record<PackName, HirePortTheme> = {
  recruitee: {
    ...defaultTheme,
    themeName: 'recruitee',
    colorPrimary: RECRUITEE_PRIMARY,
    colorSecondary: '#481692',
    colorThird: '#3F157F',
    colorBrightness: '#F6F5FA',
    colorPrimaryBg: RECRUITEE_PRIMARY_BG,
    colorItemSelectedBg: RECRUITEE_PRIMARY_BG,
    disabledSwitchColor: '#C4BCC8',
    backgroundColor: 'transparent',
    radius: RADIUS_MD,
    radiusSmall: RADIUS_MD,
  },
  jobylon: {
    ...defaultTheme,
    themeName: 'jobylon',
    colorPrimary: JOBYLON_PRIMARY,
    colorSecondary: JOBYLON_SECONDARY,
    colorPrimaryBg: JOBYLON_GHOST_BG,
    colorItemSelectedBg: JOBYLON_GHOST_BG,
    colorThird: JOBYLON_SECONDARY,
    colorBrightness: JOBYLON_LOZENGE,
    disabledSwitchColor: PARTNER_DISABLED_SWITCH,
    backgroundColor: '#F9F9FA',
    radius: 3,
    radiusSmall: 1,
  },
  smartrecruiters: {
    ...defaultTheme,
    themeName: 'smartrecruiters',
    colorPrimary: SMARTRECRUITERS_PRIMARY,
    colorSecondary: SMARTRECRUITERS_SECONDARY,
    colorPrimaryBg: SMARTRECRUITERS_PRIMARY_BG,
    colorItemSelectedBg: SMARTRECRUITERS_PRIMARY_BG,
    colorThird: SMARTRECRUITERS_SECONDARY,
    colorBrightness: '#F8FAF9',
    disabledSwitchColor: PARTNER_DISABLED_SWITCH,
    backgroundColor: '#ffffff',
    radius: RADIUS_SM,
    radiusSmall: RADIUS_SM,
  },
  hibob: {
    ...defaultTheme,
    themeName: 'hibob',
    colorPrimary: HIBOB_PRIMARY,
    colorSecondary: HIBOB_PRIMARY_HOVER,
    colorPrimaryBg: HIBOB_PRIMARY_BG,
    colorItemSelectedBg: HIBOB_PRIMARY_BG,
    colorThird: HIBOB_PRIMARY_DARK,
    colorBrightness: HIBOB_CAPPUCCINO,
    disabledSwitchColor: HIBOB_GREY_600,
    backgroundColor: HIBOB_CAPPUCCINO,
    radius: RADIUS_SM,
    radiusSmall: RADIUS_SM,
  },
};

function toPartnerDarkTheme(
  light: HirePortTheme,
  brand: Pick<HirePortTheme, 'colorSecondary' | 'colorThird'>,
): HirePortTheme {
  return {
    ...light,
    ...hireportDarkColors,
    ...hireportDarkSurfaces,
    colorPrimary: light.colorPrimary,
    colorSecondary: brand.colorSecondary,
    colorThird: brand.colorThird,
    colorPrimaryBg: mixHex(
      light.colorPrimary,
      hireportDarkSurfaces.colorBgContainer,
      0.2,
    ),
    colorItemSelectedBg: mixHex(
      light.colorPrimary,
      hireportDarkSurfaces.colorBgContainer,
      0.2,
    ),
    disabledSwitchColor: hireportDarkColors.colorMuted,
    backgroundColor: 'transparent',
  };
}

export const partnerDarkThemes: Record<PackName, HirePortTheme> = {
  recruitee: {
    ...toPartnerDarkTheme(partnerThemes.recruitee, {
      colorSecondary: RECRUITEE_DARK_PRIMARY_HOVER,
      colorThird: RECRUITEE_DARK_PRIMARY_ACTIVE,
    }),
    colorPrimary: RECRUITEE_DARK_PRIMARY,
    colorText: RECRUITEE_DARK_TEXT,
    colorSubtle: '#a3a4a8',
    colorMuted: '#808285',
    colorFaint: '#646668',
    colorBrightness: RECRUITEE_DARK_BG,
    colorBgContainer: RECRUITEE_DARK_BG,
    colorBgElevated: '#3f4042',
    borderColor: '#49494b',
    controlBorderColor: '#5b5c5d',
    colorPrimaryBg: mixHex(RECRUITEE_DARK_PRIMARY, RECRUITEE_DARK_BG, 0.2),
    colorItemSelectedBg: mixHex(RECRUITEE_DARK_PRIMARY, RECRUITEE_DARK_BG, 0.2),
    disabledSwitchColor: '#808285',
  },
  jobylon: toPartnerDarkTheme(partnerThemes.jobylon, {
    colorSecondary: '#60A5FA',
    colorThird: '#3B82F6',
  }),
  smartrecruiters: toPartnerDarkTheme(partnerThemes.smartrecruiters, {
    colorSecondary: '#4ADE80',
    colorThird: '#166534',
  }),
  hibob: toPartnerDarkTheme(partnerThemes.hibob, {
    colorSecondary: HIBOB_PRIMARY_HOVER,
    colorThird: HIBOB_PRIMARY_DARK,
  }),
};
